/**
 * Régua da tela inicial (v2.1). Mesmo espírito de
 * `PlanExtensionFormulary/tokens.ts`: valor num lugar só, nunca solto no
 * componente. Diferença de convenção deliberada: aqui não há hex literal —
 * só chaves do tema MUI (`error.main`, `success.main`, `primary.main`,
 * `text.secondary`), resolvidas depois via `sx`. A paleta desta tela é a
 * do tema (`theme-list.ts`), não uma paleta própria.
 *
 * Três eixos de cor, e confundi-los foi o defeito da primeira versão deste
 * desenho:
 *   - ALARME (`error.main`) — só o estado `CONDUCCO`: sua régua (rail) de 3px
 *     à esquerda e seu contador. O campo que carrega este eixo é `rail`, e ele
 *     é `null` em todo estado que não seja `CONDUCCO`.
 *   - ACENTO (`primary.main`) — foco, hover, "olhe aqui primeiro". Uma tela
 *     construída só de cinzas não tem hierarquia; o acento faz trabalho
 *     estrutural e nunca sinaliza severidade.
 *   - SUCESSO (`success.main`) — só `EM_DIA`, e só na zona "Saudáveis".
 *     Campo próprio (`successRail`), separado de `rail` de propósito: o eixo de
 *     alarme não pode ganhar um segundo dono por descuido de nomenclatura.
 *
 * O eixo de acento ficou narrado aqui em cima mas nunca virou campo — lacuna
 * fechada no T8: `accent` abaixo é o único lugar que carrega `primary.main`.
 * Componente nenhum deve escrever `"primary.main"` solto; todos importam
 * `HOME_TOKENS.accent`.
 *
 * DOSE do eixo de sucesso: a pergunta que a tela responde é "há tarefa nossa
 * hoje?", então a zona de alarme tem que continuar sendo a primeira coisa que o
 * olho encontra. O verde entra em opacidade baixa e SEM tingimento de fundo —
 * o `CONDUCCO` tem régua opaca MAIS bloco tingido, dois degraus acima. Verde
 * saturado aqui reintroduziria, pelo avesso, o defeito que derrubou a tela
 * antiga: ruído colorido disputando com o que importa.
 */
export const HOME_TOKENS = {
  /** Único portador do eixo de acento (T8) — ver nota acima. */
  accent: "primary.main",
  space: {
    /** Entre as sublinhas de uma mesma linha. */
    withinRow: 4,
    /** Entre linhas de uma mesma zona. */
    betweenRows: 16,
    /** Entre zonas (falhas / parados / confiabilidade). */
    betweenZones: 32,
  },
  filter: {
    /** Mesma largura já praticada em `PortfolioPage.tsx` para o campo de busca. */
    fieldWidth: 280,
    /** Mesma altura já praticada em `PortfolioPage.tsx` para os controles de filtro. */
    controlHeight: 40,
    gap: 16,
  },
  row: {
    /** Alvo de toque — WCAG 2.5.5 e dedo. */
    minHeight: 44,
    railWidth: 3,
    /** `StateIcon` — 14px fixos, controle exato do traço em `currentColor`. */
    iconSize: 14,
  },
  state: {
    /**
     * `tintHover` (Achado E, review final v2.1, 2026-08-20): a linha usava
     * `action.hover` no hover, o que trocava o tingimento de alarme por cinza
     * neutro — o hover apagava o alarme justamente quando o operador aponta
     * pra linha. Hover é eixo de ACENTO e não pode sobrescrever o eixo de
     * ALARME; aqui ele só aprofunda o mesmo `error.main`.
     */
    CONDUCCO: { rail: "error.main", tint: 0.05, tintHover: 0.1, icon: null, color: "error.main" },
    /**
     * `successRail` NÃO é `rail`: o eixo de alarme continua com um dono só.
     * `successRailOpacity` é o que mantém a dose contida — a régua verde é
     * visível sem chamar antes da vermelha, que é opaca e ainda vem com bloco
     * tingido atrás. Quem decide se a régua aparece é a ZONA, não o estado:
     * `EM_DIA` também ocorre dentro de "Em configuração", que é informativo e
     * não é conquista (ver `highlightSuccess` em `HealthyRow`).
     */
    EM_DIA: { rail: null, successRail: "success.main", successRailOpacity: 0.45, tint: 0, icon: "check", color: "success.main" },
    PARADO: { rail: null, tint: 0, icon: "pause", color: "text.secondary" },
  },
  motion: {
    /** Única transição da tela. O componente respeita `prefers-reduced-motion`. */
    hoverMs: 200,
  },
} as const;
